import { useTranslation } from 'react-i18next';

import {
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	BarChart,
	Bar
} from 'recharts';
import {
	Badge,
	Button,
	Card,
	LoadingContent,
	NoData
} from 'src/containers/Elements';
import { useJobInsight } from 'src/hooks/useJob';
import { Colors } from 'src/types/Color';
import { useForm } from 'react-hook-form';
import { DatePickerController } from 'src/components/elements/control/DatePickerController';
import { FormGroup, Label, Row, Col } from 'reactstrap';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const Statistic = () => {
	const { t } = useTranslation();
	const { job_id } = useParams();
	const [activeTab, setActiveTab] = useState<'views' | 'applied'>('views');
	// Date range form for last 90 days
	const ninetyDaysAgo = new Date();
	ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
	const today = new Date();

	const {
		control,
		getValues,
		formState: { errors }
	} = useForm({
		defaultValues: {
			fromDate: ninetyDaysAgo,
			toDate: today
		}
	});

	const { mutate, data, isLoading } = useJobInsight();

	useEffect(() => {
		if (job_id) {
			mutate({
				jobId: job_id,
				fromDate: ninetyDaysAgo.toISOString(),
				toDate: today.toISOString()
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [job_id]);
	const submitHandler = () => {
		if (job_id) {
			mutate({
				jobId: job_id, // Use dynamic job_id from useParams
				fromDate: getValues('fromDate').toISOString(),
				toDate: getValues('toDate').toISOString()
			});
		}
	};

	const appliedSources = useMemo(() => {
		if (!data) return [];
		return Array.from(
			new Set(data.map((item: any) => String(item.sourceView)))
		);
	}, [data]);

	const groupedData = useMemo(() => {
		if (!data) return [];
		const byDate: Record<string, any> = {};
		data.forEach((item: any) => {
			const dateKey = new Date(item.date).toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric'
			});
			if (!byDate[dateKey]) byDate[dateKey] = { date: dateKey };
			byDate[dateKey][`${item.sourceView}_view`] = item.jobViewCount;
			byDate[dateKey][`${item.sourceView}_applied`] =
				item.totalAppliedJob;
		});
		return Object.values(byDate);
	}, [data]);

	const barColors = [
		'#3b82f6', // blue
		'#10b981', // green
		'#8b5cf6', // purple
		'#f59e42', // orange
		'#ef4444', // red
		'#facc15', // yellow
		'#6366f1' // indigo
	];

	const getColorForSource = (source: string, idx: number) =>
		barColors[idx % barColors.length];

	return (
		<div>
			<form>
				<Row className="g-2 align-items-center ">
					<Col xs={12} md={3} lg={2}>
						<FormGroup className="mb-0">
							<Label htmlFor="fromDate">{t('forms.from')}</Label>
							<DatePickerController
								name="fromDate"
								control={control}
								error={errors.fromDate}
								maxDate={today}
								placeholder={t('forms.select-date')}
							/>
						</FormGroup>
					</Col>
					<Col xs={12} md={3} lg={2}>
						<FormGroup className="mb-0">
							<Label htmlFor="toDate">{t('forms.to')}</Label>
							<DatePickerController
								name="toDate"
								control={control}
								error={errors.toDate}
								maxDate={today}
								placeholder={t('forms.select-date')}
							/>
						</FormGroup>
					</Col>
					<Col
						xs={12}
						md={2}
						lg={1}
						className="d-flex align-items-end">
						<Button
							type="button"
							onClick={submitHandler}
							color={Colors.primary}
							className="w-100 mt-2">
							{t('button.submit')}
						</Button>
					</Col>
				</Row>
			</form>
			<Card className="d-flex flex-column gap-3 py-4">
				{isLoading && (
					<div className="p-5">
						<LoadingContent />
					</div>
				)}

				{data?.length > 0 && (
					<>
						<Row className="g-2">
							<Col xs={12} md={6} className="mb-2 mb-md-0">
								<Badge
									color={Colors['green']}
									className="d-flex align-items-center gap-2 p-2 fs-5 w-100 rounded-5 justify-content-between">
									<div className="d-flex gap-2 align-items-center">
										<span className="fs-2">
											{data?.reduce(
												(sum: number, item: any) =>
													sum +
													(Number(
														item.jobViewCount
													) || 0),
												0
											)}
										</span>
										<div className="d-flex flex-column">
											<span style={{ fontSize: '14px' }}>
												{t('label.views')}
											</span>
										</div>
									</div>
								</Badge>
							</Col>
							<Col xs={12} md={6}>
								<Badge
									color={Colors['association']}
									className="d-flex align-items-center gap-2 p-2 fs-5 w-100 justify-content-between">
									<div className="d-flex gap-2 align-items-center">
										<span className="fs-2">
											{data?.reduce(
												(sum: number, item: any) =>
													sum +
													(Number(
														item.totalAppliedJob
													) || 0),
												0
											)}
										</span>
										<div className="d-flex flex-column">
											<span style={{ fontSize: '14px' }}>
												{t('label.total-apply')}
											</span>
										</div>
									</div>
								</Badge>
							</Col>
						</Row>
					</>
				)}
				{!data?.length && !isLoading ? (
					<NoData />
				) : (
					<>
						{data?.length > 0 && (
							<div className="mb-3 d-flex gap-2">
								<Button
									color={
										activeTab === 'views'
											? Colors['primary']
											: Colors['white-green']
									}
									onClick={() => setActiveTab('views')}>
									{t('label.views')}
								</Button>

								<Button
									color={
										activeTab === 'applied'
											? Colors['primary']
											: Colors['white-green']
									}
									onClick={() => {
										setActiveTab('applied');
									}}>
									{t('label.applied')}
								</Button>
							</div>
						)}
						{data?.length > 0 && (
							<div style={{ marginLeft: 0 }}>
								<ResponsiveContainer width="100%" height={340}>
									<BarChart
										data={groupedData}
										barCategoryGap={
											groupedData.length <= 2
												? '40%'
												: '10%'
										}
										barGap={
											groupedData?.length <= 2 ? 20 : 4
										}
										margin={{
											top: 20,
											right: 30,
											left: 0,
											bottom: 20
										}}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis
											dataKey="date"
											tick={{
												fontWeight: 600,
												fontSize: 14
											}}
										/>
										<YAxis
											tick={{
												fontWeight: 600,
												fontSize: 14
											}}
										/>
										<Tooltip
											cursor={{
												fill: '#e6f4ea',
												opacity: 0.25
											}}
											itemStyle={{
												color: '#3a5a40',
												fontWeight: 500
											}}
											labelStyle={{
												color: '#588157',
												fontWeight: 700
											}}
											formatter={(value, name) => {
												if (typeof name === 'string') {
													if (name.endsWith('_view'))
														return [
															`${value}`,
															`${t(
																'label.views'
															)} (${name.replace(
																'_view',
																''
															)})`
														];
													if (
														name.endsWith(
															'_applied'
														)
													)
														return [
															`${value}`,
															`Applied (${name.replace(
																'_applied',
																''
															)})`
														];
												}
												return [value, String(name)];
											}}
										/>
										<Legend
											wrapperStyle={{
												fontWeight: 500,
												fontSize: 14
											}}
										/>
										{appliedSources.map((source, idx) =>
											activeTab === 'views' ? (
												<Bar
													key={`${source}_view`}
													dataKey={`${source}_view`}
													fill={getColorForSource(
														String(source),
														idx
													)}
													name={`${t(
														'label.views'
													)} (${String(source)})`}
													barSize={40}
													strokeWidth={1}
												/>
											) : (
												<Bar
													key={`${source}_applied`}
													dataKey={`${source}_applied`}
													fill={
														getColorForSource(
															String(source),
															idx
														) + '99'
													}
													name={`${t(
														'label.applied'
													)} (${String(source)})`}
													opacity={0.7}
													barSize={40}
													strokeDasharray="4 2"
													strokeWidth={1}
												/>
											)
										)}
									</BarChart>
								</ResponsiveContainer>
							</div>
						)}
					</>
				)}
			</Card>
		</div>
	);
};

export default Statistic;
